import { maxBy, min, minBy, max, last, uniqBy, compact } from 'lodash'
import moment from 'moment'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { getChartDate, getMaxProvisionalDate, getSubstractTime } from '../../../components/echart/EChartUtils'
import HydrometryAction from '../../actions/HydrometryAction'
import DtoHydroMeasures from '../../dto/chronicMeasures/DtoHydroMeasures'
import DtoHydroStats from '../../dto/chronicMeasures/DtoHydroStats'
import DtoHydrometricStation from '../../dto/DtoHydrometricStation'
import DtoHydrometryThreshold from '../../dto/DtoHydrometryThreshold'
import DtoEvent from '../../../events/dto/DtoEvent'
import EventsAction from '../../../events/actions/EventsAction'
import ChartReviewHydro from './ChartReviewHydro'
import ProgressCard from '../../../components/card/ProgressCard'
import { Card, CardContent, Grid } from '@mui/material'
import IAEauAction from '../../../iaeau/IAEauAction'
import SimpleChartTabsHydro from '../../../components/echart/SimpleChartTabsHydro'
import HydrometerStationAction from 'station/actions/HydrometerStationAction'
import MUIChartTabs from '../../../components/echart/MUIChartTabs'
import { HISTO, J90 } from '../../../alerting/constants/ChartFollowContants'
import useAccountSetting from '../../../utils/customHook/useAccountSetting'
import { CHART_SELECTED_TIME } from '../../../quality/constants/ChartConstant'
import ChartTabPredData from '../../../station/components/iaeauModels/ChartTabPredData'
import { getQualificationSelectOptions, getStatusSelectOptions } from '../../../utils/StatusUtil'
import { ALL, AUTO, AVERAGE, MAX, MIN, OTHER, RAW, SUM_DAY } from '../../../utils/constants/MeasureConstants'
import { HYDRO_DISPLAY_COTE, HYDRO_MEASURE_COTE, HYDRO_TAB_DISPLAY_MODES } from 'hydrometry/constants/HydrometryConstants'
import useLocalStorage from 'utils/customHook/useLocalStorage'
import { useMeasuresTempRef } from '../../../utils/customHook/useMeasureTempRef'
import ChartTempRefChip from '../../../components/echart/ChartTempRefChip'
import { HYDRO_L_STORAGE, HYDRO_TYPEID } from '../../constants/HydrometryConstants'
import { RETURN_PERIOD_STATS, buildHydroStatsSeries } from '../suivi/utils/StatsSeriesUtilsHydro'

const SUPPR = 'suppr'
const PERSONALIZED_GROUPING = 'personalizedGrouping'
const HYDROMETER_SAMPLING_TYPE = -2

// TODO ATTENTION ! Composant réutilisé dans plusieurs écrans
const HydroChartPanel = ({
    id,
    showLegend,
    isPopin = true,
    useFromSituation = false,
    withReturnPeriods = false,
}) => {
    const lastTime = useAccountSetting(CHART_SELECTED_TIME, v => v === HISTO ? v : (v ? parseInt(v) : J90))
    const lastSelectedTime = lastTime !== 'HISTO' ? lastTime : J90
    const [time, setTime] = useState(lastSelectedTime || J90)
    const getDefaultMinDate = () => lastSelectedTime === HISTO ? undefined : getSubstractTime(lastSelectedTime)
    const getDefaultMaxDate = () => lastSelectedTime === HISTO ? moment().endOf('day').valueOf() : undefined

    const [fullScreen, setFullScreen] = useState(false)
    const [minDate, setMinDate] = useState(getDefaultMinDate())
    const [maxDate, setMaxDate] = useState(getDefaultMaxDate())
    const [dataLoaded, setDataLoaded] = useState(false)
    const [hydroStatistics, setHydroStatistics] = useState([])
    const [progress, setProgress] = useState(0)
    const [returnPeriodSeries, setReturnPeriodSeries] = useState([])
    const [displayCote, setDisplayCote] = useLocalStorage(HYDRO_DISPLAY_COTE, HYDRO_MEASURE_COTE.HEIGHT)
    const [displayModes, setDisplayModes] = useLocalStorage(HYDRO_TAB_DISPLAY_MODES, {
        auto: true,
        max: false,
        min: false,
        average: false,
        brute: false,
        initialBrute: false,
        suppr: false,
    })
    const [accuracy, setAccuracy] = useState({
        qualifications: getQualificationSelectOptions().map(q => q.code),
        status: getStatusSelectOptions().map(s => s.code),
    })
    const [modelData, setModelData] = useState({ predMeasures: [], selectedPred: undefined, predDate: undefined, predStats: [] })
    const [tempRef, setTempRef] = useLocalStorage(HYDRO_L_STORAGE.TEMP_REF, undefined)


    const {
        hydroMeasures,
        hydrometricStation,
    } = useSelector(store => ({
        hydroMeasures: store.HydrometryReducer.hydroMeasures,
        hydrometricStation: store.HydrometryReducer.hydrometricStation,
    }), shallowEqual)

    const stationTempRefOffset = useMeasuresTempRef(hydrometricStation?.link_tempRefs, hydroStatistics.map(stat => stat.typeId))
    const tempRefOffset = useMeasuresTempRef(hydrometricStation?.link_tempRefs, hydroStatistics.map(stat => stat.typeId), tempRef)

    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(HydrometryAction.fetchHydrometricThresholds())
        dispatch(HydrometryAction.fetchHydrometryDataTypes())
        dispatch(HydrometryAction.fetchHydrometricStation(id))
        dispatch(HydrometryAction.fetchHydroStatistics(id)).then((params = []) => setHydroStatistics(params.map(stats => new DtoHydroStats(stats))))
        dispatch(EventsAction.fetchStationEvents('hydrometry', id))
        dispatch(IAEauAction.getModels('hydrometry', parseInt(id)))
        dispatch(HydrometerStationAction.fetchHydrometerChartOptions(id))
        if (withReturnPeriods) {
            buildHydroStatsSeries({
                id,
                typeId: HYDRO_TYPEID.HEIGHT,
                statSeries: RETURN_PERIOD_STATS,
            }).then(setReturnPeriodSeries)
        }
    }, []) // eslint-disable-next-line react-hooks/exhaustive-deps

    const minDateFilterPreview = (date) => {
        const allFirstMeasures = hydroMeasures.flatMap(obj => obj.measures.length ? [obj.measures[0].date] : [])
        return date ?? min(allFirstMeasures) ?? minBy(hydroStatistics, 'startDate')?.startDate ?? moment().subtract(1, 'years').valueOf()
    }

    const maxDateFilterPreview = (date) => {
        if (date) {
            return date
        }
        const lastMeasureDate = maxBy([...hydroStatistics, ...hydroMeasures], 'endDate')?.endDate
        const todaysDay = moment().endOf('day').valueOf()
        const maxDatePredDataTmp = modelData && modelData.selectedPred && max(Object.keys(modelData.selectedPred).map(k => last(modelData.predMeasures[k])?.date))
        const maxDatePredData = maxDatePredDataTmp ? moment(maxDatePredDataTmp).add(1, 'day').endOf('day').valueOf() : todaysDay
        const tmpMax = lastMeasureDate && lastMeasureDate > todaysDay ? getMaxProvisionalDate(time, minDate, lastMeasureDate) : todaysDay
        return max([tmpMax, maxDatePredData])
    }

    useEffect(() => {
        setDataLoaded(false)
        const defaultGroupMode = [90, 365, HISTO, OTHER].includes(time) ? MAX : ALL
        // les mesures brutes initiales ne sont proposées qu'en validation : le choix, partagé via le localStorage, est ignoré ici
        const selectedModes = compact([
            displayModes.auto && AUTO,
            displayModes.brute && RAW,
            displayModes.max && MAX,
            displayModes.min && MIN,
            displayModes.average && AVERAGE,
            displayModes.suppr && SUPPR,
            displayModes.personalizedGrouping && displayModes.personalizedGroupingValue,
        ])
        const hydroModes = selectedModes.length ? selectedModes : [AUTO]
        const inputs = uniqBy(hydroStatistics, 'typeId').flatMap(stat => {
            return hydroModes.map(h => {
                const groupFunc = h === AUTO ? defaultGroupMode : h
                const groupFuncIsSumDay = stat.typeId === HYDROMETER_SAMPLING_TYPE ? SUM_DAY : groupFunc
                return {
                    stationId: id,
                    dataType: stat.typeId,
                    groupFunc: groupFuncIsSumDay === PERSONALIZED_GROUPING ? displayModes.personalizedGroupingValue : groupFunc.toUpperCase(),
                    chartMode: true,
                    startDate: minDate,
                    endDate: time === HISTO ? undefined : maxDate,
                    status: accuracy.status,
                    qualifications: accuracy.qualifications,
                    tempRefOffsetHours: tempRefOffset,
                }
            })
        })
        dispatch(HydrometryAction.loadHydroChronicMeasures(inputs, p => setProgress(p))).then(() => setDataLoaded(true))
    }, [hydroStatistics, time, displayModes, accuracy, tempRefOffset])

    const fullScreenStyle = (isPopin && fullScreen) ? {
        position: 'fixed',
        paddingTop: '1rem',
        top: '64px',
        left: 0,
        height: !isPopin ? `calc(${window.innerHeight}px - 64px)` : `calc(${window.innerHeight}px)`,
        width: `calc(${window.innerWidth}px)`,
        zIndex: 9,
        marginTop: !isPopin ? '0' : '-64px',
        overflowY: 'auto',
        backgroundColor: 'white',
    } : {}

    if (!hydroStatistics.length) {
        return null
    }

    const { minDate: formattedMinDate, maxDate: formattedMaxDate } = getChartDate(minDateFilterPreview(minDate), maxDateFilterPreview(maxDate), time)

    return (
        <Card sx={{ boxShadow: '0 0' }}>
            <CardContent>
                <Grid container sx={fullScreenStyle}>
                    <Grid size={12} className={(!isPopin && fullScreen) ? 'fullscreen-chart' : ''}>
                        <Grid sx={{ paddingLeft: '48px' }} container alignItems='center'>
                            <Grid container>
                                <MUIChartTabs
                                    offsetHours={tempRefOffset}
                                    time={time}
                                    onChangeTime={(newTime) => {
                                        setMinDate(newTime.minDate)
                                        setMaxDate(newTime.maxDate)
                                        setTime(newTime.time)
                                    }}
                                />
                            </Grid>
                            <Grid>
                                <ChartTabPredData
                                    stationType='hydrometry'
                                    id={id}
                                    setParentModelData={setModelData}
                                    useFromSituation={useFromSituation}
                                />
                            </Grid>
                            <Grid sx={{ paddingLeft: '20px' }}>
                                <SimpleChartTabsHydro
                                    displayCote={displayCote}
                                    displayModes={displayModes}
                                    changeDisplayCote={setDisplayCote}
                                    changeDisplayModes={setDisplayModes}
                                    displayModesUniqChoise={false}
                                    changeAccuracy={setAccuracy}
                                    hideAccuracy={false}
                                />
                            </Grid>
                            <Grid sx={{ paddingLeft: '10px' }}>
                                <ChartTempRefChip
                                    tempRef={tempRef}
                                    offsetHours={tempRefOffset}
                                    stationOffsetHours={stationTempRefOffset}
                                    stationTempRefs={hydrometricStation?.link_tempRefs}
                                    onChange={setTempRef}
                                />
                            </Grid>
                        </Grid>
                        <Grid>
                            {dataLoaded ? (
                                <ChartReviewHydro
                                    id={id}
                                    tempRef={tempRef}
                                    hydroStatistics={hydroStatistics}
                                    fullScreen={fullScreen}
                                    setFullScreen={setFullScreen}
                                    hydroMeasures={hydroMeasures}
                                    minDate={formattedMinDate}
                                    maxDate={formattedMaxDate}
                                    showLegend={showLegend}
                                    isPopin={isPopin}
                                    modelData={modelData}
                                    displayModes={displayModes}
                                    displayCote={displayCote}
                                    returnPeriodSeries={returnPeriodSeries}
                                />
                            ) : <ProgressCard progress={progress} />}
                        </Grid>
                    </Grid>
                </Grid>
            </CardContent>
        </Card >
    )
}

HydroChartPanel.propTypes = {
    id: PropTypes.number,
    hydrometricStation: PropTypes.instanceOf(DtoHydrometricStation),
    loadHydroChronicMeasures: PropTypes.func,
    hydroStatistics: PropTypes.arrayOf(PropTypes.instanceOf(DtoHydroStats)),
    hydroMeasures: PropTypes.arrayOf(PropTypes.instanceOf(DtoHydroMeasures)),
    hydrometryThresholds: PropTypes.arrayOf(PropTypes.instanceOf(DtoHydrometryThreshold)),
    fetchHydrometricThresholds: PropTypes.func,
    stationEvents: PropTypes.arrayOf(PropTypes.instanceOf(DtoEvent)),
    fetchHydrometryDataTypes: PropTypes.func,
    showLegend: PropTypes.bool,
    isPopin: PropTypes.bool,
    withReturnPeriods: PropTypes.bool,
    useFromSituation: PropTypes.bool, // permet de charger des données de prévision sur la situation (ne pas retirer)
}

export default HydroChartPanel
